import { ArrowDown, ArrowUp } from '@carbon/icons-react';
import { Button, Checkbox, InlineLoading, InlineNotification, Layer, Stack, Tag } from '@carbon/react';
import { asProblem, problemMessage } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ErrorNotice } from '../common/Notices';
import { move } from './emailModel';
import { listRoutes, MESSAGE_CHANNELS, routesKey, saveRoute, type MessageChannel, type MessageEvent, type MessageRoute } from './messagingApi';

/**
 * The channels each customer message goes by, first tried first (ADR 0125), for the whole company: a channel is used when the brand
 * has an account and a template for it and the customer has the address; otherwise, or when the provider can't reach the person,
 * the next one is tried.
 */
export function MessageRoutesPanel({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const routes = useQuery({ queryKey: routesKey, queryFn: listRoutes });
  return (
    <Stack gap={5}>
      <p className="pos-messages__secondary">{t('messaging.routes.intro')}</p>
      {routes.error && <ErrorNotice title={problemMessage(t, asProblem(routes.error))} onRetry={() => void routes.refetch()} />}
      {routes.isLoading && <InlineLoading description={t('common.loading')} />}
      <div className="pos-message-routes">
        {(routes.data ?? []).map((route) => (
          // A saved order comes back with a new version: the row starts again from it.
          <RouteCard key={`${route.eventCode}:${route.version}`} route={route} canEdit={canEdit} />
        ))}
      </div>
    </Stack>
  );
}

type Row = { channel: string; on: boolean };

/** The route's channels in their order, then the ones it doesn't use. */
const rowsOf = (route: MessageRoute): Row[] => [
  ...route.channels.map((channel) => ({ channel, on: true })),
  ...MESSAGE_CHANNELS.filter((c) => !route.channels.includes(c)).map((channel) => ({ channel, on: false })),
];

function RouteCard({ route, canEdit }: { route: MessageRoute; canEdit: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [rows, setRows] = useState(() => rowsOf(route));
  const [saved, setSaved] = useState(false);
  const channels = rows.filter((r) => r.on).map((r) => r.channel);
  const changed = channels.join() !== route.channels.join();
  const event = t(`messaging.events.${route.eventCode as MessageEvent}`, { defaultValue: route.eventCode });
  const save = useMutation({
    mutationFn: () => saveRoute(route.eventCode, channels, route.version),
    onSuccess: async () => {
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: routesKey });
    },
  });
  const channelName = (c: string) => t(`messaging.channels.${c as MessageChannel}`, { defaultValue: c });

  return (
    <Layer>
      <section className="pos-message-route" aria-label={event}>
        <div className="pos-message-route__head">
          <h3 className="pos-message-route__title">{event}</h3>
          {route.isDefault && (
            <Tag type="cool-gray" size="sm">
              {t('messaging.routes.default')}
            </Tag>
          )}
        </div>
        <ol className="pos-message-route__channels">
          {rows.map((r, i) => (
            <li key={r.channel} className="pos-message-route__channel">
              <Checkbox
                id={`route-${route.eventCode}-${r.channel}`}
                labelText={channelName(r.channel)}
                checked={r.on}
                disabled={!canEdit}
                onChange={(_, { checked }) => {
                  setSaved(false);
                  setRows(rows.map((x) => (x.channel === r.channel ? { ...x, on: checked } : x)));
                }}
              />
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={ArrowUp}
                iconDescription={`${t('messaging.routes.up')}: ${channelName(r.channel)}`}
                disabled={!canEdit || i === 0}
                onClick={() => {
                  setSaved(false);
                  setRows(move(rows, i, i - 1));
                }}
              />
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={ArrowDown}
                iconDescription={`${t('messaging.routes.down')}: ${channelName(r.channel)}`}
                disabled={!canEdit || i === rows.length - 1}
                onClick={() => {
                  setSaved(false);
                  setRows(move(rows, i, i + 1));
                }}
              />
            </li>
          ))}
        </ol>
        {channels.length === 0 && <p className="pos-messages__secondary">{t('messaging.routes.noneOn')}</p>}
        {save.error && (
          <InlineNotification kind="error" lowContrast hideCloseButton title={problemMessage(t, asProblem(save.error))} />
        )}
        {canEdit && (
          <div className="pos-message-route__actions">
            {save.isPending ? (
              <InlineLoading description={t('messaging.routes.saving')} />
            ) : saved && !changed ? (
              <InlineLoading status="finished" description={t('messaging.routes.saved')} />
            ) : (
              <Button
                size="sm"
                kind="secondary"
                disabled={!changed || channels.length === 0}
                onClick={() => save.mutate()}
              >
                {t('messaging.routes.save')}
              </Button>
            )}
          </div>
        )}
      </section>
    </Layer>
  );
}
